import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const NormalizationSettings = ({ 
  settings = {}, 
  onSettingsChange,
  onSaveTemplate,
  onLoadTemplate 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('text');

  const currencyOptions = [
    { value: 'USD', label: 'US Dollar ($)', description: 'Default currency format' },
    { value: 'EUR', label: 'Euro (€)', description: 'European currency format' },
    { value: 'GBP', label: 'British Pound (£)', description: 'UK currency format' },
    { value: 'CAD', label: 'Canadian Dollar (C$)', description: 'Canadian currency format' },
    { value: 'AUTO', label: 'Auto-detect', description: 'Detect from data patterns' }
  ];

  const numberFormatOptions = [
    { value: 'US', label: '1,234.56', description: 'US/UK format (comma thousands, period decimal)' },
    { value: 'EU', label: '1.234,56', description: 'European format (period thousands, comma decimal)' },
    { value: 'SPACE', label: '1 234.56', description: 'Space thousands, period decimal' },
    { value: 'AUTO', label: 'Auto-detect', description: 'Detect from data patterns' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY', description: 'US format (10/17/2025)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY', description: 'European format (17/10/2025)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD', description: 'ISO format (2025-10-17)' },
    { value: 'AUTO', label: 'Auto-detect', description: 'Detect from data patterns' }
  ];

  const textCleaningRules = [
    { 
      key: 'trimWhitespace', 
      label: 'Trim whitespace', 
      description: 'Remove leading and trailing spaces' 
    },
    { 
      key: 'removeExtraSpaces', 
      label: 'Remove extra spaces', 
      description: 'Replace multiple spaces with single space' 
    },
    { 
      key: 'standardizeCase', 
      label: 'Standardize case', 
      description: 'Convert to title case for product names' 
    },
    { 
      key: 'removeBrandDuplicates', 
      label: 'Remove brand duplicates', 
      description: 'Remove brand name from product name if already in brand field' 
    },
    { 
      key: 'standardizeUnits', 
      label: 'Standardize units', 
      description: 'Convert units to standard format (kg, cm, etc.)' 
    },
    { 
      key: 'removeSpecialChars', 
      label: 'Clean special characters', 
      description: 'Remove or replace non-standard characters' 
    }
  ];

  const numericCleaningRules = [
    { 
      key: 'removeCurrencySymbols', 
      label: 'Remove currency symbols', 
      description: 'Strip $, €, £ and other currency symbols' 
    },
    { 
      key: 'standardizeDecimals', 
      label: 'Standardize decimals', 
      description: 'Convert all decimal separators to periods' 
    },
    { 
      key: 'removeThousandsSeparators', 
      label: 'Remove thousands separators', 
      description: 'Remove commas, periods, or spaces used as thousands separators' 
    },
    { 
      key: 'handleNegativeNumbers', 
      label: 'Handle negative numbers', 
      description: 'Standardize negative number formats' 
    }
  ];

  const handleSettingChange = (key, value) => {
    onSettingsChange({
      ...settings,
      [key]: value
    });
  };

  const handleRuleToggle = (ruleKey, checked) => {
    const currentRules = settings?.textCleaningRules || {};
    handleSettingChange('textCleaningRules', {
      ...currentRules,
      [ruleKey]: checked
    });
  };

  const handleNumericRuleToggle = (ruleKey, checked) => {
    const currentRules = settings?.numericCleaningRules || {};
    handleSettingChange('numericCleaningRules', {
      ...currentRules,
      [ruleKey]: checked
    });
  };

  const getEnabledRulesCount = () => {
    const textRules = Object.values(settings?.textCleaningRules || {})?.filter(Boolean)?.length;
    const numericRules = Object.values(settings?.numericCleaningRules || {})?.filter(Boolean)?.length;
    return textRules + numericRules;
  };

  const tabs = [
    { id: 'text', label: 'Text Cleaning', icon: 'Type' },
    { id: 'numeric', label: 'Numeric Format', icon: 'Hash' },
    { id: 'templates', label: 'Templates', icon: 'Save' }
  ];

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Data Normalization</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Configure data cleaning and formatting rules ({getEnabledRulesCount()} rules active)
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? 'ChevronUp' : 'ChevronDown'}
            iconPosition="left"
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </Button>
        </div>
      </div>
      {/* Quick Settings (Always Visible) */}
      <div className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Currency Format"
            options={currencyOptions}
            value={settings?.currencyFormat || 'USD'}
            onChange={(value) => handleSettingChange('currencyFormat', value)}
          />
          <Select
            label="Number Format"
            options={numberFormatOptions}
            value={settings?.numberFormat || 'US'}
            onChange={(value) => handleSettingChange('numberFormat', value)}
          />
        </div>
      </div>
      {/* Expanded Settings */}
      {isExpanded && (
        <div className="border-t border-border">
          {/* Tabs */}
          <div className="flex border-b border-border">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`
                  flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-colors
                  ${activeTab === tab?.id 
                    ? 'text-primary border-b-2 border-primary bg-primary bg-opacity-5' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }
                `}
              >
                <Icon name={tab?.icon} size={16} />
                <span>{tab?.label}</span>
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="p-4">
            {activeTab === 'text' && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-3">Text Cleaning Rules</h4>
                  <div className="space-y-3">
                    {textCleaningRules?.map((rule) => (
                      <Checkbox
                        key={rule?.key}
                        label={rule?.label}
                        description={rule?.description}
                        checked={settings?.textCleaningRules?.[rule?.key] || false}
                        onChange={(e) => handleRuleToggle(rule?.key, e?.target?.checked)}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'numeric' && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-3">Numeric Processing Rules</h4>
                  <div className="space-y-3">
                    {numericCleaningRules?.map((rule) => (
                      <Checkbox
                        key={rule?.key}
                        label={rule?.label}
                        description={rule?.description}
                        checked={settings?.numericCleaningRules?.[rule?.key] || false}
                        onChange={(e) => handleNumericRuleToggle(rule?.key, e?.target?.checked)}
                      />
                    ))}
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <Select
                    label="Date Format"
                    description="Format for parsing date fields"
                    options={dateFormatOptions}
                    value={settings?.dateFormat || 'AUTO'}
                    onChange={(value) => handleSettingChange('dateFormat', value)}
                  />
                </div>
              </div>
            )}

            {activeTab === 'templates' && (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-foreground mb-3">Mapping Templates</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Save current settings as a template for future uploads
                  </p>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={onSaveTemplate}
                      iconName="Save"
                      iconPosition="left"
                    >
                      Save Current Settings
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onLoadTemplate}
                      iconName="Upload"
                      iconPosition="left"
                    >
                      Load Template
                    </Button>
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <h5 className="text-sm font-medium text-foreground mb-2">Recent Templates</h5>
                  <div className="space-y-2">
                    {['Electronics Template', 'Furniture Template', 'Default Template']?.map((template, index) => (
                      <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                        <span className="text-sm text-foreground">{template}</span>
                        <div className="flex items-center space-x-1">
                          <Button
                            variant="ghost"
                            size="xs"
                            iconName="Download"
                            className="h-6 w-6"
                          />
                          <Button
                            variant="ghost"
                            size="xs"
                            iconName="Trash2"
                            className="h-6 w-6 text-error hover:text-error"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Footer */}
      <div className="p-4 border-t border-border bg-muted bg-opacity-30">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center space-x-4">
            <span className="text-muted-foreground">
              <span className="font-medium text-foreground">{getEnabledRulesCount()}</span> cleaning rules active
            </span>
            <span className="text-muted-foreground">
              Format: <span className="font-medium text-foreground">{settings?.currencyFormat || 'USD'}</span>
            </span>
          </div>
          <div className="flex items-center space-x-1">
            <Icon name="Zap" size={12} className="text-warning" />
            <span className="text-muted-foreground">
              Auto-normalization enabled
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NormalizationSettings;